"use client";

import * as React from "react";
import { XAxis, YAxis, XAxisProps, YAxisProps } from "recharts";
import { xAxisConfig, yAxisConfig } from "@/utils";
import type { AxisValueFormat } from "@/utils/format-axis-value";

export type ChartXAxisOptions = {
  label?: string;
  hideLabel?: boolean;
} & Partial<XAxisProps>;

/**
 * Recharts only discovers axis config from direct XAxis/YAxis elements in the
 * chart tree — wrapper components are ignored. Use this factory in JSX:
 * `{chartXAxis()}`
 */
export function chartXAxis({
  label = "Time [Days]",
  hideLabel = true,
  ...options
}: ChartXAxisOptions = {}) {
  const { height = 40, ...rest } = options;

  return React.createElement(XAxis, {
    ...xAxisConfig({ height, ...rest }),
    label: hideLabel
      ? undefined
      : {
          value: label,
          position: "insideBottom",
          offset: -2,
          fill: "rgba(255,255,255,0.55)",
          fontSize: 11,
          style: { textAnchor: "middle" },
        },
  });
}

export type ChartYAxisOptions = {
  label: string;
  logScale?: boolean;
  valueFormat?: AxisValueFormat;
  hideLabel?: boolean;
} & Partial<YAxisProps>;

export function chartYAxis({
  label,
  logScale = true,
  valueFormat = "compact",
  hideLabel = false,
  ...options
}: ChartYAxisOptions) {
  return React.createElement(YAxis, {
    key: logScale ? "y-log" : "y-linear",
    ...yAxisConfig({ logScale, valueFormat, ...options }),
    label: hideLabel
      ? undefined
      : {
          value: label,
          angle: -90,
          position: "insideLeft",
          offset: 12,
          fill: "rgba(255,255,255,0.55)",
          fontSize: 11,
          style: { textAnchor: "middle" },
        },
  });
}
